/********************************************************************
 * ADOBE CONFIDENTIAL
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  OrderContext,
  OrderDataModel,
  ShoppingCartContext,
} from '@/order/data/models';

const sumDiscounts = (accum: number, discount: any): number => {
  return accum + discount.amount.value;
};

export const transformShoppingCartContext = (
  cartId: string,
  data: OrderDataModel
): ShoppingCartContext => {
  return {
    id: cartId,
    totalQuantity: data.totalQuantity,
    possibleOnepageCheckout: true,
    // @ts-ignore
    items: data.items.map((orderItem) => ({
      // we don't know if the checkout drop-in can apply Manufacturer’s Suggested Retail Price
      canApplyMsrp: true,
      formattedPrice: '',
      id: orderItem.id,
      quantity: orderItem.totalQuantity,
      product: {
        canonicalUrl: orderItem.product?.canonicalUrl,
        mainImageUrl: orderItem.product?.image ?? '',
        name: orderItem.product?.name ?? '',
        productId: 0, // productId not exposed by core GraphQL as number (uid is string)
        productType: orderItem.product?.productType,
        sku: orderItem.product?.sku ?? '',
        topLevelSku: orderItem.product?.sku,
      },
      prices: {
        price: {
          value: orderItem.price.value,
          currency: orderItem.price.currency,
          regularPrice: orderItem.regularPrice?.value ?? orderItem.price.value,
        },
      },
      configurableOptions:
        orderItem.selectedOptions?.map((selectedOption) => ({
          optionLabel: selectedOption.label,
          valueLabel: selectedOption.value,
        })) || [],
      discountAmount:
        (orderItem.itemPrices?.discounts ?? orderItem.prices?.discounts)
          ?.reduce(sumDiscounts, 0) ?? 0,
    })),
    prices: {
      subtotalExcludingTax: {
        value: data.subtotalExclTax.value,
        currency: data.subtotalExclTax.currency,
      },
      subtotalIncludingTax: {
        value: data.subtotalInclTax.value,
        currency: data.subtotalInclTax.currency,
      },
    },
    discountAmount: data.discounts.reduce(sumDiscounts, 0),
  };
};

export const transformOrderContext = (data: OrderDataModel): OrderContext => {
  const firstCoupon = data.coupons[0];
  // for now, we only support one payment per order
  const orderPayment = data.payments?.[0];

  return {
    appliedCouponCode: firstCoupon?.code ?? '',
    email: data.email,
    grandTotal: data.grandTotal.value,
    orderId: data.number,
    orderType: 'checkout',
    otherTax: 0,
    salesTax: data.totalTax.value,
    shipping: {
      shippingMethod: data.shipping?.code ?? '',
      shippingAmount: data.shipping?.amount ?? 0,
    },
    subtotalExcludingTax: data.subtotalExclTax.value,
    subtotalIncludingTax: data.subtotalInclTax.value,
    payments: orderPayment
      ? [
          {
            paymentMethodCode: orderPayment?.code || '',
            paymentMethodName: orderPayment?.name || '',
            total: data.grandTotal.value,
            orderId: data.number,
          },
        ]
      : [],
    discountAmount: data.discounts.reduce(sumDiscounts, 0),
    taxAmount: data.totalTax.value,
    paymentAmount: data.grandTotal.value,
    priceTotal: data.grandTotal.value,
  };
};
